/**
 * Report inbound CPC query params as `/dye` events (yoto `reportCPCValues` /
 * ecom-redirect `reportCpc`).
 *
 * - `obcpc`: plain float × 1.02 × active ad account `cpc_multiplier`
 * - `tblcpc`: opaque encrypted string forwarded as-is, plus Taboola pixel
 *   metadata (`tblpxid` = pixel_id, `pxtid` = pixel_tag_id) so analytics can
 *   decrypt server-side. The browser does not decrypt.
 */

import type { AdAccountConfig } from '../config/types';
import type { PageParams } from './pageParams';
import { track } from './track';
import { debug } from '../../util/log';

const CPC_SENT_KEY = 'kz_cpc_sent';

/** Outbrain gets 1.02x; Taboola encrypted payload has no client multiplier. */
const CPC_MULTIPLIERS: [keyof Pick<PageParams, 'outbrainCpc' | 'taboolaCpc'>, number | null, string][] = [
  ['outbrainCpc', 1.02, 'obcpc'],
  ['taboolaCpc', null, 'tblcpc'],
];

function alreadySent(): boolean {
  try {
    return sessionStorage.getItem(CPC_SENT_KEY) === '1';
  } catch {
    return false;
  }
}

function markSent(): void {
  try {
    sessionStorage.setItem(CPC_SENT_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function reportCpcValues(
  pageParams: PageParams,
  adAccount: AdAccountConfig | null,
): void {
  if (alreadySent()) return;
  if (!pageParams.outbrainCpc && !pageParams.taboolaCpc) return;

  for (const [pageKey, baseMultiplier, eventType] of CPC_MULTIPLIERS) {
    const rawValue = pageParams[pageKey];
    if (!rawValue) continue;

    try {
      const params: Record<string, string> = {};

      if (eventType === 'tblcpc') {
        params.tblcpc = String(rawValue);
        const pixel = adAccount?.pixel;
        if (pixel?.platform === 'taboola') {
          params.tblpxid = pixel.pixel_id;
          if (pixel.pixel_tag_id) params.pxtid = pixel.pixel_tag_id;
        }
      } else {
        let value = parseFloat(rawValue);
        if (!Number.isFinite(value) || value <= 0) continue;
        if (baseMultiplier !== null) value *= baseMultiplier;
        if (typeof adAccount?.cpcMultiplier === 'number' && Number.isFinite(adAccount.cpcMultiplier)) {
          value *= adAccount.cpcMultiplier;
        }
        params.obcpc = String(value);
      }

      track(eventType, params);
      markSent();
      debug('cpc reported', { type: eventType, params });
      return;
    } catch {
      /* ignore and try next key */
    }
  }
}
